import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { BOARD_TOP_Y, CUP_RIM_Y, GAME_CONFIG } from '../game/config';
import type {
  CupDescriptor,
  PhysicsFrame,
  PhysicsImpact,
  ThrowOutcome,
} from '../game/types';

const FIXED_TIMESTEP = 1 / 60;
const MAX_FRAME_DELTA = 0.1;
const BALL_MASS = 0.025;
const WALL_ROUNDING = 0.006;
const IMPACT_COOLDOWN = 0.075;
const MAX_IMPACTS_PER_FRAME = 6;

const BALL_RESTITUTION = 0.74;
const BALL_FRICTION = 0.18;
// Keep flight aligned with the analytic aim solution. Contact friction and
// angular damping still remove energy once the ball reaches the cup field.
const BALL_LINEAR_DAMPING = 0;
const BALL_ANGULAR_DAMPING = 0.22;

// The shallow concave interior guides a centered throw into the base instead of
// acting like a second springboard. The exposed flange remains deliberately lively.
const WALL_RESTITUTION = 0.16;
const WALL_FRICTION = 0.4;
const RIM_RESTITUTION = 0.84;
const RIM_FRICTION = 0.14;
const BOTTOM_RESTITUTION = 0.1;
const BOTTOM_FRICTION = 0.62;
const BOARD_RESTITUTION = 0.48;
const BOARD_FRICTION = 0.42;

const COUPELLE_BASE_HEIGHT = 0.028;
const COUPELLE_FLOOR_Y = 0.048;
const COUPELLE_WALL_START_Y = 0.04;
const COUPELLE_RIM_HALF_HEIGHT = 0.012;
const SPECIAL_RIM_HEIGHT_SCALE = 1.55;

const Y_AXIS = new THREE.Vector3(0, 1, 0);

let rapierInitialization: Promise<void> | null = null;

function ensureRapierInitialized(): Promise<void> {
  rapierInitialization ??= RAPIER.init();
  return rapierInitialization;
}

type ImpactSurface = PhysicsImpact['surface'];

type ColliderTag = {
  surface: ImpactSurface | 'safety';
  cupIndex: number | null;
};

type CupInstances = {
  body: THREE.InstancedMesh;
  rim: THREE.InstancedMesh;
  base: THREE.InstancedMesh;
};

type CupColors = {
  normalBody: THREE.Color;
  normalAccent: THREE.Color;
  specialBody: THREE.Color;
  specialAccent: THREE.Color;
  claimedBody: THREE.Color;
  claimedAccent: THREE.Color;
};

export type CupPhysicsDiagnostics = {
  engine: 'Rapier 3D';
  initialized: boolean;
  fixedTimestep: number;
  accumulator: number;
  simulationSeconds: number;
  bodyCount: number;
  colliderCount: number;
  cupBodyCount: number;
  collidersPerCup: number;
  ccdBodyCount: number;
  activeBall: boolean;
  ballElapsed: number;
  ballPosition: { x: number; y: number; z: number } | null;
  ballSpeed: number;
  ballAngularSpeed: number;
  settlementCupIndex: number | null;
  settlementSeconds: number;
  claimedCupCount: number;
  impactsEmitted: number;
  lastOutcome: {
    kind: ThrowOutcome['kind'];
    cupIndex: number | null;
    scoreAwarded: number;
  } | null;
};

/**
 * Owns the complete cup-toss simulation and its directly corresponding visuals.
 *
 * The render loop should call `step` once per frame. Internally the world always
 * advances at 60 Hz; the ball mesh is synchronized exactly once after stepping.
 */
export class CupPhysicsSystem {
  readonly group = new THREE.Group();
  readonly cups: readonly CupDescriptor[];

  private readonly colliderTags = new Map<number, ColliderTag>();
  private readonly claimedCups = new Set<number>();
  private readonly ownedGeometries: THREE.BufferGeometry[] = [];
  private readonly ownedMaterials: THREE.Material[] = [];
  private readonly impactTimes = new Map<string, number>();
  private readonly cupColors: CupColors;
  private readonly ballVisual = new THREE.Group();
  private cupInstances: CupInstances | null = null;

  private world: RAPIER.World | null = null;
  private eventQueue: RAPIER.EventQueue | null = null;
  private ballBody: RAPIER.RigidBody | null = null;
  private ballCollider: RAPIER.Collider | null = null;
  private initializePromise: Promise<void> | null = null;

  private accumulator = 0;
  private simulationSeconds = 0;
  private ballElapsed = 0;
  private settlementCupIndex: number | null = null;
  private settlementSeconds = 0;
  private ballResolved = false;
  private impactsEmitted = 0;
  private lastOutcome: ThrowOutcome | null = null;
  private disposed = false;

  constructor() {
    this.group.name = 'cup-physics-system';
    this.cups = this.createCupDescriptors();
    this.cupColors = this.createCupColors();
    this.createVisuals();
  }

  async initialize(): Promise<void> {
    if (this.disposed) throw new Error('Cannot initialize a disposed CupPhysicsSystem.');
    if (this.world) return;

    this.initializePromise ??= this.initializeInternal();
    await this.initializePromise;
  }

  /** Replaces any previous throw with one CCD-enabled, physically simulated ball. */
  launchBall(position: THREE.Vector3, velocity: THREE.Vector3): void {
    const world = this.requireWorld();
    this.removeBall();

    const angularVelocity = {
      x: -velocity.z * 0.55,
      y: velocity.x * 0.15,
      z: velocity.x * 0.55,
    };
    const bodyDescription = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(position.x, position.y, position.z)
      .setLinvel(velocity.x, velocity.y, velocity.z)
      .setAngvel(angularVelocity)
      .setLinearDamping(BALL_LINEAR_DAMPING)
      .setAngularDamping(BALL_ANGULAR_DAMPING)
      .setAdditionalSolverIterations(4)
      .setCanSleep(true)
      .setCcdEnabled(true);

    this.ballBody = world.createRigidBody(bodyDescription);
    const colliderDescription = RAPIER.ColliderDesc.ball(GAME_CONFIG.ballRadius)
      .setMass(BALL_MASS)
      .setRestitution(BALL_RESTITUTION)
      .setFriction(BALL_FRICTION)
      .setActiveEvents(RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS)
      .setContactForceEventThreshold(0);
    this.ballCollider = world.createCollider(colliderDescription, this.ballBody);

    this.ballVisual.visible = true;
    this.ballVisual.position.copy(position);
    this.ballVisual.quaternion.identity();
    this.ballElapsed = 0;
    this.accumulator = 0;
    this.settlementCupIndex = null;
    this.settlementSeconds = 0;
    this.ballResolved = false;
    this.lastOutcome = null;
    this.impactTimes.clear();
  }

  /**
   * Advances fixed physics, returns one-shot impacts and at most one one-shot outcome.
   */
  step(deltaSeconds: number): PhysicsFrame {
    const impacts: PhysicsImpact[] = [];
    let outcome: ThrowOutcome | null = null;

    if (!this.world || !this.eventQueue || !this.ballBody) {
      return { impacts, outcome };
    }

    const safeDelta = Number.isFinite(deltaSeconds)
      ? THREE.MathUtils.clamp(deltaSeconds, 0, MAX_FRAME_DELTA)
      : 0;
    this.accumulator += safeDelta;

    while (this.accumulator >= FIXED_TIMESTEP) {
      this.world.timestep = FIXED_TIMESTEP;
      this.world.step(this.eventQueue);
      this.accumulator -= FIXED_TIMESTEP;
      this.simulationSeconds += FIXED_TIMESTEP;
      this.ballElapsed += FIXED_TIMESTEP;

      this.collectImpactEvents(impacts);
      if (!this.ballResolved && outcome === null) {
        outcome = this.evaluateOutcome();
        if (outcome) {
          this.ballResolved = true;
          this.lastOutcome = outcome;
        }
      }
    }

    this.synchronizeBallVisual();
    return { impacts, outcome };
  }

  hasActiveBall(): boolean {
    return this.ballBody !== null;
  }

  getBallPosition(target: THREE.Vector3): THREE.Vector3 | null {
    if (!this.ballBody) return null;
    const position = this.ballBody.translation();
    return target.set(position.x, position.y, position.z);
  }

  removeBall(): void {
    if (this.world && this.ballBody) {
      this.world.removeRigidBody(this.ballBody);
    }
    this.ballBody = null;
    this.ballCollider = null;
    this.ballVisual.visible = false;
    this.ballElapsed = 0;
    this.accumulator = 0;
    this.settlementCupIndex = null;
    this.settlementSeconds = 0;
    this.ballResolved = false;
    this.impactTimes.clear();
    this.eventQueue?.clear();
  }

  resetRound(): void {
    this.removeBall();
    this.claimedCups.clear();
    this.lastOutcome = null;
    for (const cup of this.cups) this.applyCupColors(cup.index, false);
    this.markCupColorsForUpload();
  }

  /** Marks a cup as already awarded and gives it a subdued claimed treatment. */
  markCupClaimed(index: number): void {
    if (!Number.isInteger(index) || index < 0 || index >= this.cups.length) {
      throw new RangeError(`Cup index ${index} is outside the cup grid.`);
    }
    this.claimedCups.add(index);
    this.applyCupColors(index);
  }

  getDiagnostics(): CupPhysicsDiagnostics {
    const position = this.ballBody?.translation() ?? null;
    const velocity = this.ballBody?.linvel() ?? null;
    const angularVelocity = this.ballBody?.angvel() ?? null;

    return {
      engine: 'Rapier 3D',
      initialized: this.world !== null,
      fixedTimestep: FIXED_TIMESTEP,
      accumulator: this.accumulator,
      simulationSeconds: this.simulationSeconds,
      bodyCount: this.world?.bodies.len() ?? 0,
      colliderCount: this.world?.colliders.len() ?? 0,
      cupBodyCount: this.world ? this.cups.length : 0,
      collidersPerCup: GAME_CONFIG.cupSegments * 2 + 1,
      ccdBodyCount: this.ballBody ? 1 : 0,
      activeBall: this.ballBody !== null,
      ballElapsed: this.ballElapsed,
      ballPosition: position ? { x: position.x, y: position.y, z: position.z } : null,
      ballSpeed: velocity ? Math.hypot(velocity.x, velocity.y, velocity.z) : 0,
      ballAngularSpeed: angularVelocity
        ? Math.hypot(angularVelocity.x, angularVelocity.y, angularVelocity.z)
        : 0,
      settlementCupIndex: this.settlementCupIndex,
      settlementSeconds: this.settlementSeconds,
      claimedCupCount: this.claimedCups.size,
      impactsEmitted: this.impactsEmitted,
      lastOutcome: this.lastOutcome
        ? {
            kind: this.lastOutcome.kind,
            cupIndex: this.lastOutcome.cup?.index ?? null,
            scoreAwarded: this.lastOutcome.scoreAwarded,
          }
        : null,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.removeBall();
    this.eventQueue?.free();
    this.eventQueue = null;
    this.world?.free();
    this.world = null;
    this.colliderTags.clear();
    this.cupInstances?.body.dispose();
    this.cupInstances?.rim.dispose();
    this.cupInstances?.base.dispose();
    this.cupInstances = null;
    this.group.clear();
    for (const geometry of this.ownedGeometries) geometry.dispose();
    for (const material of this.ownedMaterials) material.dispose();
    this.disposed = true;
  }

  private async initializeInternal(): Promise<void> {
    await ensureRapierInitialized();
    if (this.disposed) throw new Error('CupPhysicsSystem was disposed during initialization.');

    this.world = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
    this.world.timestep = FIXED_TIMESTEP;
    this.world.numSolverIterations = 6;
    this.world.maxCcdSubsteps = 2;
    this.eventQueue = new RAPIER.EventQueue(true);

    this.createBoardCollider();
    this.createSafetyFloorCollider();
    this.createCupColliders();
  }

  private createCupDescriptors(): readonly CupDescriptor[] {
    const descriptors: CupDescriptor[] = [];
    const specials = new Set<number>(GAME_CONFIG.specialCupIndices);
    const startX =
      GAME_CONFIG.boardCenter.x - ((GAME_CONFIG.columns - 1) * GAME_CONFIG.cupSpacing) / 2;
    const startZ =
      GAME_CONFIG.boardCenter.z - ((GAME_CONFIG.rows - 1) * GAME_CONFIG.cupSpacing) / 2;

    for (let row = 0; row < GAME_CONFIG.rows; row += 1) {
      for (let column = 0; column < GAME_CONFIG.columns; column += 1) {
        const index = row * GAME_CONFIG.columns + column;
        descriptors.push({
          index,
          row,
          column,
          kind: specials.has(index) ? 'special' : 'normal',
          // The public center denotes the opening, which is the useful aim/readback point.
          center: new THREE.Vector3(
            startX + column * GAME_CONFIG.cupSpacing,
            CUP_RIM_Y,
            startZ + row * GAME_CONFIG.cupSpacing,
          ),
        });
      }
    }
    return Object.freeze(descriptors);
  }

  private createCupColors(): CupColors {
    return {
      normalBody: new THREE.Color('#f3ead8'),
      normalAccent: new THREE.Color('#7cc8cb'),
      specialBody: new THREE.Color('#d42f3f'),
      specialAccent: new THREE.Color('#f6c64e'),
      claimedBody: new THREE.Color('#654950'),
      claimedAccent: new THREE.Color('#837454'),
    };
  }

  private createVisuals(): void {
    const boardGeometry = new THREE.BoxGeometry(
      GAME_CONFIG.boardSize.x,
      GAME_CONFIG.boardSize.y,
      GAME_CONFIG.boardSize.z,
    );
    const boardMaterial = new THREE.MeshStandardMaterial({
      color: '#17475a',
      roughness: 0.58,
      metalness: 0.04,
    });
    const trimGeometry = new THREE.BoxGeometry(
      GAME_CONFIG.boardSize.x + 0.2,
      GAME_CONFIG.boardSize.y * 0.48,
      GAME_CONFIG.boardSize.z + 0.2,
    );
    const trimMaterial = new THREE.MeshStandardMaterial({
      color: '#e5a73e',
      roughness: 0.4,
      metalness: 0.12,
    });
    this.ownedGeometries.push(boardGeometry, trimGeometry);
    this.ownedMaterials.push(boardMaterial, trimMaterial);

    const trim = new THREE.Mesh(trimGeometry, trimMaterial);
    trim.position.set(
      GAME_CONFIG.boardCenter.x,
      GAME_CONFIG.boardCenter.y - GAME_CONFIG.boardSize.y * 0.34,
      GAME_CONFIG.boardCenter.z,
    );
    trim.castShadow = true;
    trim.receiveShadow = true;
    trim.name = 'raised-board-trim';
    this.group.add(trim);

    const board = new THREE.Mesh(boardGeometry, boardMaterial);
    board.position.set(
      GAME_CONFIG.boardCenter.x,
      GAME_CONFIG.boardCenter.y,
      GAME_CONFIG.boardCenter.z,
    );
    board.castShadow = true;
    board.receiveShadow = true;
    board.name = 'raised-cup-board';
    this.group.add(board);

    const cupBodyGeometry = this.createCupBodyGeometry();
    const rimGeometry = this.createCupRimGeometry();
    const baseGeometry = new THREE.CylinderGeometry(
      GAME_CONFIG.cupBottomRadius + 0.018,
      GAME_CONFIG.cupBottomRadius + 0.008,
      COUPELLE_BASE_HEIGHT,
      GAME_CONFIG.cupSegments * 2,
    );
    this.ownedGeometries.push(cupBodyGeometry, rimGeometry, baseGeometry);

    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: '#ffffff',
      roughness: 0.29,
      metalness: 0.04,
    });
    const rimMaterial = new THREE.MeshStandardMaterial({
      color: '#ffffff',
      roughness: 0.25,
      metalness: 0.2,
    });
    const baseMaterial = new THREE.MeshStandardMaterial({
      color: '#ffffff',
      roughness: 0.3,
      metalness: 0.12,
    });
    this.ownedMaterials.push(bodyMaterial, rimMaterial, baseMaterial);

    const bodyInstances = new THREE.InstancedMesh(
      cupBodyGeometry,
      bodyMaterial,
      this.cups.length,
    );
    const rimInstances = new THREE.InstancedMesh(
      rimGeometry,
      rimMaterial,
      this.cups.length,
    );
    const baseInstances = new THREE.InstancedMesh(
      baseGeometry,
      baseMaterial,
      this.cups.length,
    );
    this.cupInstances = {
      body: bodyInstances,
      rim: rimInstances,
      base: baseInstances,
    };

    bodyInstances.name = 'cup-bodies';
    rimInstances.name = 'cup-rims';
    baseInstances.name = 'cup-bases';
    for (const instances of [bodyInstances, rimInstances, baseInstances]) {
      instances.castShadow = true;
      instances.receiveShadow = true;
      instances.instanceMatrix.setUsage(THREE.StaticDrawUsage);
    }

    const instanceMatrix = new THREE.Matrix4();
    for (const cup of this.cups) {
      instanceMatrix.makeTranslation(cup.center.x, BOARD_TOP_Y, cup.center.z);
      bodyInstances.setMatrixAt(cup.index, instanceMatrix);
      const rimHeightScale = cup.kind === 'special' ? SPECIAL_RIM_HEIGHT_SCALE : 1;
      instanceMatrix.makeScale(1, rimHeightScale, 1);
      instanceMatrix.setPosition(
        cup.center.x,
        BOARD_TOP_Y + GAME_CONFIG.cupHeight - COUPELLE_RIM_HALF_HEIGHT * rimHeightScale,
        cup.center.z,
      );
      rimInstances.setMatrixAt(cup.index, instanceMatrix);
      instanceMatrix.makeTranslation(
        cup.center.x,
        BOARD_TOP_Y + COUPELLE_BASE_HEIGHT / 2,
        cup.center.z,
      );
      baseInstances.setMatrixAt(cup.index, instanceMatrix);
      this.applyCupColors(cup.index, false);
    }

    for (const instances of [bodyInstances, rimInstances, baseInstances]) {
      instances.instanceMatrix.needsUpdate = true;
      if (instances.instanceColor) instances.instanceColor.setUsage(THREE.DynamicDrawUsage);
      instances.computeBoundingBox();
      instances.computeBoundingSphere();
    }
    this.markCupColorsForUpload();
    this.group.add(baseInstances, bodyInstances, rimInstances);

    this.createBallVisual();
  }

  /** A shallow, curved plastic coupelle shell with a broad concave interior. */
  private createCupBodyGeometry(): THREE.LatheGeometry {
    const height = GAME_CONFIG.cupHeight;
    const outerBottom = GAME_CONFIG.cupBottomRadius;
    const innerBottom = outerBottom - GAME_CONFIG.cupWallThickness;
    const outerTop = GAME_CONFIG.cupInnerRadius + GAME_CONFIG.cupWallThickness;
    const innerTop = GAME_CONFIG.cupInnerRadius;
    const profile = [
      new THREE.Vector2(outerBottom + 0.008, 0.012),
      new THREE.Vector2(outerBottom + 0.014, COUPELLE_BASE_HEIGHT),
      new THREE.Vector2(outerBottom, COUPELLE_FLOOR_Y + 0.004),
      new THREE.Vector2(THREE.MathUtils.lerp(outerBottom, outerTop, 0.2), height * 0.27),
      new THREE.Vector2(THREE.MathUtils.lerp(outerBottom, outerTop, 0.46), height * 0.45),
      new THREE.Vector2(THREE.MathUtils.lerp(outerBottom, outerTop, 0.74), height * 0.64),
      new THREE.Vector2(THREE.MathUtils.lerp(outerBottom, outerTop, 0.92), height * 0.79),
      new THREE.Vector2(outerTop, height - COUPELLE_RIM_HALF_HEIGHT * 1.8),
      new THREE.Vector2(outerTop + 0.004, height - COUPELLE_RIM_HALF_HEIGHT),
      new THREE.Vector2(innerTop, height - COUPELLE_RIM_HALF_HEIGHT),
      new THREE.Vector2(innerTop - 0.002, height - COUPELLE_RIM_HALF_HEIGHT * 2.1),
      new THREE.Vector2(
        THREE.MathUtils.lerp(innerBottom, innerTop, 0.86),
        height * 0.76,
      ),
      new THREE.Vector2(
        THREE.MathUtils.lerp(innerBottom, innerTop, 0.62),
        height * 0.55,
      ),
      new THREE.Vector2(
        THREE.MathUtils.lerp(innerBottom, innerTop, 0.3),
        height * 0.3,
      ),
      new THREE.Vector2(innerBottom, COUPELLE_FLOOR_Y + 0.004),
      new THREE.Vector2(0, COUPELLE_FLOOR_Y),
      new THREE.Vector2(0, 0.012),
      new THREE.Vector2(outerBottom + 0.008, 0.012),
    ];
    const geometry = new THREE.LatheGeometry(profile, GAME_CONFIG.cupSegments * 3);
    geometry.computeVertexNormals();
    return geometry;
  }

  /** A flattened saucer flange; special instances stretch it vertically in gold. */
  private createCupRimGeometry(): THREE.LatheGeometry {
    const inner = GAME_CONFIG.cupInnerRadius;
    const outer = GAME_CONFIG.cupTopRadius;
    const halfHeight = COUPELLE_RIM_HALF_HEIGHT;
    const profile = [
      new THREE.Vector2(inner + 0.006, -halfHeight),
      new THREE.Vector2(inner, -halfHeight * 0.25),
      new THREE.Vector2(inner + 0.005, halfHeight),
      new THREE.Vector2(outer - 0.008, halfHeight),
      new THREE.Vector2(outer, halfHeight * 0.25),
      new THREE.Vector2(outer - 0.006, -halfHeight),
      new THREE.Vector2(inner + 0.006, -halfHeight),
    ];
    const geometry = new THREE.LatheGeometry(profile, GAME_CONFIG.cupSegments * 3);
    geometry.computeVertexNormals();
    return geometry;
  }

  private createBallVisual(): void {
    const ballGeometry = new THREE.SphereGeometry(
      GAME_CONFIG.ballRadius,
      24,
      16,
    );
    const seamGeometry = new THREE.TorusGeometry(
      GAME_CONFIG.ballRadius * 0.995,
      GAME_CONFIG.ballRadius * 0.04,
      6,
      36,
    );
    const ballMaterial = new THREE.MeshStandardMaterial({
      color: '#fffdf4',
      roughness: 0.36,
      metalness: 0.01,
    });
    const seamMaterial = new THREE.MeshStandardMaterial({
      color: '#d93c46',
      roughness: 0.32,
      metalness: 0.02,
    });
    this.ownedGeometries.push(ballGeometry, seamGeometry);
    this.ownedMaterials.push(ballMaterial, seamMaterial);

    const ball = new THREE.Mesh(ballGeometry, ballMaterial);
    const seamA = new THREE.Mesh(seamGeometry, seamMaterial);
    const seamB = new THREE.Mesh(seamGeometry, seamMaterial);
    seamB.rotation.x = Math.PI / 2;
    ball.castShadow = true;
    seamA.castShadow = true;
    seamB.castShadow = true;
    this.ballVisual.add(ball, seamA, seamB);
    this.ballVisual.visible = false;
    this.ballVisual.name = 'throw-ball';
    this.group.add(this.ballVisual);
  }

  private applyCupColors(index: number, markForUpload = true): void {
    const instances = this.cupInstances;
    const cup = this.cups[index];
    if (!instances || !cup) return;

    let bodyColor: THREE.Color;
    let accentColor: THREE.Color;
    if (this.claimedCups.has(index)) {
      bodyColor = this.cupColors.claimedBody;
      accentColor = this.cupColors.claimedAccent;
    } else {
      bodyColor =
        cup.kind === 'special' ? this.cupColors.specialBody : this.cupColors.normalBody;
      accentColor =
        cup.kind === 'special' ? this.cupColors.specialAccent : this.cupColors.normalAccent;
    }

    instances.body.setColorAt(index, bodyColor);
    instances.rim.setColorAt(index, accentColor);
    instances.base.setColorAt(index, accentColor);
    if (markForUpload) this.markCupColorsForUpload();
  }

  private markCupColorsForUpload(): void {
    if (!this.cupInstances) return;
    for (const instances of [
      this.cupInstances.body,
      this.cupInstances.rim,
      this.cupInstances.base,
    ]) {
      if (instances.instanceColor) instances.instanceColor.needsUpdate = true;
    }
  }

  private createBoardCollider(): void {
    const world = this.requireWorld();
    const body = world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation(
        GAME_CONFIG.boardCenter.x,
        GAME_CONFIG.boardCenter.y,
        GAME_CONFIG.boardCenter.z,
      ),
    );
    const descriptor = RAPIER.ColliderDesc.cuboid(
      GAME_CONFIG.boardSize.x / 2,
      GAME_CONFIG.boardSize.y / 2,
      GAME_CONFIG.boardSize.z / 2,
    )
      .setRestitution(BOARD_RESTITUTION)
      .setFriction(BOARD_FRICTION)
      .setActiveEvents(RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS)
      .setContactForceEventThreshold(0);
    const collider = world.createCollider(descriptor, body);
    this.colliderTags.set(collider.handle, { surface: 'board', cupIndex: null });
  }

  private createSafetyFloorCollider(): void {
    const world = this.requireWorld();
    const body = world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation(0, -0.2, GAME_CONFIG.boardCenter.z * 0.25),
    );
    const descriptor = RAPIER.ColliderDesc.cuboid(14, 0.15, 18)
      .setRestitution(0.16)
      .setFriction(0.72);
    const collider = world.createCollider(descriptor, body);
    this.colliderTags.set(collider.handle, { surface: 'safety', cupIndex: null });
  }

  private createCupColliders(): void {
    const world = this.requireWorld();
    const segmentCount = GAME_CONFIG.cupSegments;
    const rimHalfRadial = (GAME_CONFIG.cupTopRadius - GAME_CONFIG.cupInnerRadius) / 2;
    const rimCenterRadius = (GAME_CONFIG.cupTopRadius + GAME_CONFIG.cupInnerRadius) / 2;
    const bottomInnerRadius = GAME_CONFIG.cupBottomRadius - GAME_CONFIG.cupWallThickness;
    const wallBottomCenterRadius = bottomInnerRadius + GAME_CONFIG.cupWallThickness / 2;
    const wallTopCenterRadius = GAME_CONFIG.cupInnerRadius + GAME_CONFIG.cupWallThickness / 2;
    const wallBottomY = COUPELLE_WALL_START_Y;
    const wallTopY = GAME_CONFIG.cupHeight - COUPELLE_RIM_HALF_HEIGHT * 2;
    const wallVerticalSpan = wallTopY - wallBottomY;
    const wallRadialSpan = wallTopCenterRadius - wallBottomCenterRadius;
    const wallLength = Math.hypot(wallVerticalSpan, wallRadialSpan);
    const wallTilt = Math.atan2(wallRadialSpan, wallVerticalSpan);
    const wallCenterRadius = (wallBottomCenterRadius + wallTopCenterRadius) / 2;
    const wallCenterY = (wallBottomY + wallTopY) / 2;
    const tangentHalfWidth =
      wallTopCenterRadius * Math.tan(Math.PI / segmentCount) + 0.01;
    const rimTangentHalfWidth =
      rimCenterRadius * Math.tan(Math.PI / segmentCount) + 0.008;

    for (const cup of this.cups) {
      const body = world.createRigidBody(
        RAPIER.RigidBodyDesc.fixed()
          .setTranslation(cup.center.x, BOARD_TOP_Y, cup.center.z)
          .setUserData({ type: 'cup', cupIndex: cup.index }),
      );

      for (let segment = 0; segment < segmentCount; segment += 1) {
        const theta = (segment / segmentCount) * Math.PI * 2;
        const sinTheta = Math.sin(theta);
        const cosTheta = Math.cos(theta);

        const wallRotation = new THREE.Quaternion()
          .setFromAxisAngle(Y_AXIS, theta)
          .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), wallTilt));
        const wallDescription = RAPIER.ColliderDesc.roundCuboid(
          tangentHalfWidth - WALL_ROUNDING,
          wallLength / 2 - WALL_ROUNDING,
          GAME_CONFIG.cupWallThickness / 2 - WALL_ROUNDING,
          WALL_ROUNDING,
        )
          .setTranslation(
            sinTheta * wallCenterRadius,
            wallCenterY,
            cosTheta * wallCenterRadius,
          )
          .setRotation(wallRotation)
          .setRestitution(WALL_RESTITUTION)
          .setRestitutionCombineRule(RAPIER.CoefficientCombineRule.Multiply)
          .setFriction(WALL_FRICTION)
          .setFrictionCombineRule(RAPIER.CoefficientCombineRule.Max)
          .setActiveEvents(RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS)
          .setContactForceEventThreshold(0);
        const wallCollider = world.createCollider(wallDescription, body);
        this.colliderTags.set(wallCollider.handle, {
          surface: 'cup-wall',
          cupIndex: cup.index,
        });

        const rimHalfHeight =
          COUPELLE_RIM_HALF_HEIGHT *
          (cup.kind === 'special' ? SPECIAL_RIM_HEIGHT_SCALE : 1);
        const rimRotation = new THREE.Quaternion().setFromAxisAngle(Y_AXIS, theta);
        const rimDescription = RAPIER.ColliderDesc.roundCuboid(
          rimTangentHalfWidth - WALL_ROUNDING,
          rimHalfHeight - WALL_ROUNDING,
          rimHalfRadial - WALL_ROUNDING,
          WALL_ROUNDING,
        )
          .setTranslation(
            sinTheta * rimCenterRadius,
            GAME_CONFIG.cupHeight - rimHalfHeight,
            cosTheta * rimCenterRadius,
          )
          .setRotation(rimRotation)
          .setRestitution(RIM_RESTITUTION)
          .setFriction(RIM_FRICTION)
          .setActiveEvents(RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS)
          .setContactForceEventThreshold(0);
        const rimCollider = world.createCollider(rimDescription, body);
        this.colliderTags.set(rimCollider.handle, { surface: 'rim', cupIndex: cup.index });
      }

      const bottomDescription = RAPIER.ColliderDesc.cylinder(
        COUPELLE_FLOOR_Y / 2,
        bottomInnerRadius,
      )
        .setTranslation(0, COUPELLE_FLOOR_Y / 2, 0)
        .setRestitution(BOTTOM_RESTITUTION)
        .setRestitutionCombineRule(RAPIER.CoefficientCombineRule.Multiply)
        .setFriction(BOTTOM_FRICTION)
        .setFrictionCombineRule(RAPIER.CoefficientCombineRule.Max)
        .setActiveEvents(RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS)
        .setContactForceEventThreshold(0);
      const bottomCollider = world.createCollider(bottomDescription, body);
      this.colliderTags.set(bottomCollider.handle, {
        surface: 'cup-wall',
        cupIndex: cup.index,
      });
    }
  }

  private collectImpactEvents(impacts: PhysicsImpact[]): void {
    if (!this.eventQueue || !this.ballBody || !this.ballCollider) return;
    const ballHandle = this.ballCollider.handle;

    this.eventQueue.drainContactForceEvents((event) => {
      if (impacts.length >= MAX_IMPACTS_PER_FRAME) return;
      const handleA = event.collider1();
      const handleB = event.collider2();
      const otherHandle = handleA === ballHandle ? handleB : handleB === ballHandle ? handleA : null;
      if (otherHandle === null) return;

      const tag = this.colliderTags.get(otherHandle);
      if (!tag || tag.surface === 'safety') return;

      const velocity = this.ballBody?.linvel();
      if (!velocity) return;
      const bodySpeed = Math.hypot(velocity.x, velocity.y, velocity.z);
      const impulseSpeed = (event.totalForceMagnitude() * FIXED_TIMESTEP) / BALL_MASS;
      const speed = Math.min(18, Math.max(bodySpeed, impulseSpeed));
      const minimumSpeed = tag.surface === 'rim' ? 0.45 : tag.surface === 'cup-wall' ? 0.5 : 0.6;
      if (speed < minimumSpeed) return;

      const cooldownKey = `${tag.surface}:${tag.cupIndex ?? -1}`;
      const lastImpact = this.impactTimes.get(cooldownKey) ?? -Infinity;
      if (this.simulationSeconds - lastImpact < IMPACT_COOLDOWN) return;
      this.impactTimes.set(cooldownKey, this.simulationSeconds);

      const position = this.ballBody?.translation();
      if (!position) return;
      impacts.push({
        surface: tag.surface,
        speed,
        position: new THREE.Vector3(position.x, position.y, position.z),
      });
      this.impactsEmitted += 1;
    });
  }

  private evaluateOutcome(): ThrowOutcome | null {
    if (!this.ballBody) return null;
    const position = this.ballBody.translation();
    const velocity = this.ballBody.linvel();
    const angularVelocity = this.ballBody.angvel();
    const linearSpeed = Math.hypot(velocity.x, velocity.y, velocity.z);
    const angularSpeed = Math.hypot(angularVelocity.x, angularVelocity.y, angularVelocity.z);
    const cup = this.findContainingCup(position.x, position.y, position.z);

    if (
      cup &&
      linearSpeed <= GAME_CONFIG.settleSpeed &&
      angularSpeed <= GAME_CONFIG.settleAngularSpeed
    ) {
      if (this.settlementCupIndex === cup.index) {
        this.settlementSeconds += FIXED_TIMESTEP;
      } else {
        this.settlementCupIndex = cup.index;
        this.settlementSeconds = FIXED_TIMESTEP;
      }

      if (this.settlementSeconds >= GAME_CONFIG.settleDuration) {
        return {
          kind: cup.kind,
          cup,
          scoreAwarded:
            cup.kind === 'special' && !this.claimedCups.has(cup.index)
              ? GAME_CONFIG.specialCupScore
              : 0,
          settledFor: this.settlementSeconds,
        };
      }
    } else {
      this.settlementCupIndex = null;
      this.settlementSeconds = 0;
    }

    if (this.isOutOfBounds(position.x, position.y, position.z) || this.ballElapsed >= GAME_CONFIG.missTimeout) {
      return {
        kind: 'miss',
        cup: null,
        scoreAwarded: 0,
        settledFor: 0,
      };
    }
    return null;
  }

  private findContainingCup(x: number, y: number, z: number): CupDescriptor | null {
    const localY = y - BOARD_TOP_Y;
    const interiorBottomY = COUPELLE_FLOOR_Y;
    const interiorTopY = GAME_CONFIG.cupHeight - COUPELLE_RIM_HALF_HEIGHT;
    if (
      localY <= interiorBottomY + GAME_CONFIG.ballRadius * 0.55 ||
      localY >= GAME_CONFIG.cupHeight - GAME_CONFIG.ballRadius * 0.08
    ) {
      return null;
    }

    const startX =
      GAME_CONFIG.boardCenter.x - ((GAME_CONFIG.columns - 1) * GAME_CONFIG.cupSpacing) / 2;
    const startZ =
      GAME_CONFIG.boardCenter.z - ((GAME_CONFIG.rows - 1) * GAME_CONFIG.cupSpacing) / 2;
    const column = Math.round((x - startX) / GAME_CONFIG.cupSpacing);
    const row = Math.round((z - startZ) / GAME_CONFIG.cupSpacing);
    if (
      column < 0 ||
      column >= GAME_CONFIG.columns ||
      row < 0 ||
      row >= GAME_CONFIG.rows
    ) {
      return null;
    }

    const cup = this.cups[row * GAME_CONFIG.columns + column];
    if (!cup) return null;
    const normalizedHeight = THREE.MathUtils.clamp(
      (localY - interiorBottomY) / (interiorTopY - interiorBottomY),
      0,
      1,
    );
    const bottomInnerRadius = GAME_CONFIG.cupBottomRadius - GAME_CONFIG.cupWallThickness;
    const interiorRadius = THREE.MathUtils.lerp(
      bottomInnerRadius,
      GAME_CONFIG.cupInnerRadius,
      normalizedHeight,
    );
    const safeCenterRadius = interiorRadius - GAME_CONFIG.ballRadius * 0.55;
    const dx = x - cup.center.x;
    const dz = z - cup.center.z;
    return dx * dx + dz * dz <= safeCenterRadius * safeCenterRadius ? cup : null;
  }

  private isOutOfBounds(x: number, y: number, z: number): boolean {
    if (this.ballElapsed > 0.35 && y < BOARD_TOP_Y - 0.5) return true;

    const halfWidth = GAME_CONFIG.boardSize.x / 2;
    const halfDepth = GAME_CONFIG.boardSize.z / 2;
    const frontLimit = Math.max(
      GAME_CONFIG.boardCenter.z + halfDepth + 3,
      GAME_CONFIG.releasePosition.z + 3,
    );
    return (
      Math.abs(x - GAME_CONFIG.boardCenter.x) > halfWidth + 4 ||
      z < GAME_CONFIG.boardCenter.z - halfDepth - 4 ||
      z > frontLimit ||
      y > CUP_RIM_Y + 12 ||
      y < -2
    );
  }

  private synchronizeBallVisual(): void {
    if (!this.ballBody) return;
    const position = this.ballBody.translation();
    const rotation = this.ballBody.rotation();
    this.ballVisual.position.set(position.x, position.y, position.z);
    this.ballVisual.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
  }

  private requireWorld(): RAPIER.World {
    if (!this.world) {
      throw new Error('CupPhysicsSystem.initialize() must finish before using physics.');
    }
    return this.world;
  }
}
